import {
  selectCommentGuards,
  selectCustomerChats,
} from '@mm/state/selectors/promoterSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as MailboxIcon } from 'assets/icons/mailbox.svg';
import React from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { UserRoleContainer } from '../../../../../../common/hooks/use-route-guard';
import { TooltipGuard } from '../../../../../../modules/permissions/RequiresPermissions';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { useCreateStartBotPromoterByType } from '../../../../startBotPromoters/common/use-create-start-bot-promoter-by-type';
import './EmptyState.pcss';

type Props = {
  noConversationsToShow: boolean;
};

export const EmptyState = (props: Props) => {
  const { noConversationsToShow } = props;
  const { history } = useRouter();

  const chatbotId = useSelector(activeChatbotIdSelector);
  const customerChats = useSelector(selectCustomerChats);
  const commentGuards = useSelector(selectCommentGuards);

  const adConfigs = useSelector(
    (state: StoreState) => state.adConfigs.entities,
  );

  const createCustomerChat = useCreateStartBotPromoterByType('customer_chat');
  const createCommentGuard =
    useCreateStartBotPromoterByType('fb_comment_guard');

  const legacyAdSync = useSelector(
    (state: StoreState) => state.messengerAds.entities,
  );

  const {
    roleFlags: { isLiveChatAgent },
  } = UserRoleContainer.useContainer();

  const navigateToMessengerAds = () =>
    history.push(
      BotBuilderRoutes.campaigns.getLink({ chatbotId, kind: 'ctm_ads' }),
    );

  const promotersHelpersHash = {
    customerChatWidget: {
      details:
        'Put a chat widget on your website to start engaging with website visitors',
      button: 'Create a customer chat widget',
      handler: () => createCustomerChat(),
    },
    commentGuard: {
      details:
        'Set up a Comment Guard on your Facebook posts to send an automatic reply to customers who commented on your posts',
      button: 'Set up Comment Guard',
      handler: () => createCommentGuard(),
    },
    messengerAd: {
      details:
        'Create a Messenger Ad to reach new, qualified leads on Facebook ',
      button: 'Create a Messenger Ad',
      handler: navigateToMessengerAds,
    },
    consulting: {
      details:
        'Need a dedicated Chatbot Development Specialist? Sign up for our Premier Consulting package.',
      button: 'Learn more about Premier Consulting',
      handler: () => window.open('https://customers.ai/premier-consulting'),
    },
    filter: {
      details:
        'No conversations match your filter settings. Change your filters to see more conversations.',
      button: null,
    },
  };

  const getPromoterHelper = () => {
    if (noConversationsToShow) {
      return promotersHelpersHash.filter;
    }
    if (!customerChats.length && !isLiveChatAgent) {
      return promotersHelpersHash.customerChatWidget;
    }
    if (!commentGuards.length && !isLiveChatAgent) {
      return promotersHelpersHash.commentGuard;
    }
    if (!adConfigs.length && !legacyAdSync.length && !isLiveChatAgent) {
      return promotersHelpersHash.messengerAd;
    }
    return promotersHelpersHash.consulting;
  };

  const promoter = getPromoterHelper();

  const shouldGuard =
    promoter === promotersHelpersHash.commentGuard ||
    promoter === promotersHelpersHash.messengerAd;

  return (
    <div className="conversations-empty-state">
      <div className="conversations-empty-state--icon">
        <MailboxIcon />
      </div>
      <div className="conversations-empty-state--text">
        No conversations yet
      </div>
      <div className="conversations-empty--details">{promoter.details}</div>
      {promoter.button && (
        <TooltipGuard
          guardIf={shouldGuard}
          position="right"
          classNameArrow="conversations-empty--tooltip-error"
        >
          {isAllowed => (
            <PrimaryButton onClick={promoter.handler} disabled={!isAllowed}>
              {promoter.button}
            </PrimaryButton>
          )}
        </TooltipGuard>
      )}
    </div>
  );
};
